/**
 * Capability code list.
 * @see {@link https://docs.google.com/spreadsheets/d/1KAZXBtO1ldBl3K5CMBSGpYPQ8SWy-h4-xxdm_F1kMgo/ Source of truth (alpha-4 code format)}
 */ const CAPABILITY_CODES = [
    "abts",
    "agld",
    "agrr",
    "agso",
    "aorg",
    "auth",
    "avis",
    "awsf",
    "bbcr",
    "bccr",
    "cads",
    "ccon",
    "cldp",
    "clex",
    "clim",
    "clmg",
    "clmp",
    "clqa",
    "clrc",
    "clso",
    "cmsg",
    "cnmd",
    "cntm",
    "conp",
    "cran",
    "csnt",
    "cudt",
    "desi",
    "devp",
    "dgan",
    "docu",
    "dotl",
    "dtca",
    "dtin",
    "eccc",
    "esfu",
    "fsbo",
    "geoi",
    "hids",
    "home",
    "hpfn",
    "icpf",
    "icpm",
    "icvm",
    "idvf",
    "infg",
    "ioca",
    "iprf",
    "loci",
    "medi",
    "mkau",
    "mktk",
    "mlus",
    "mobl",
    "more",
    "noti",
    "ocoa",
    "opad",
    "owcl",
    "owpp",
    "owrn",
    "paym",
    "pdbo",
    "ppdc",
    "ppes",
    "pric",
    "prom",
    "prpk",
    "prvl",
    "ptlk",
    "pvcy",
    "qatl",
    "seom",
    "serp",
    "skld",
    "skls",
    "skpf",
    "slam",
    "slcv",
    "slls",
    "slmp",
    "sopp",
    "ssal",
    "trak",
    "uctg",
    "ucws",
    "ufrn",
    "usdt",
    "usrm",
    "vspl"
];

const B2B_BRANDS = [
    "slp"
];
const B2C_BRANDS = [
    "imn",
    "iwt",
    "li",
    "sl",
    "sln",
    "iwb",
    "iwtat"
];
const BRANDS = [
    ...B2B_BRANDS,
    ...B2C_BRANDS
];
const STAGES = [
    "dev",
    "preview",
    "live"
];
const LOCALES = [
    "en",
    "de",
    "fr",
    "nl"
];
const REMOTE_TYPES = [
    "app",
    "mfe"
];
const LOCALIZED_BRANDS = {
    iwb: [
        "en",
        "fr",
        "nl"
    ]
};
const DOMAIN_BY_BRAND_STAGE = {
    imn: {
        dev: "https://www.dev.immonet.de",
        preview: "https://www.preview.immonet.de",
        live: "https://www.immonet.de"
    },
    iwt: {
        dev: "https://dev.immowelt.de",
        preview: "https://preview.immowelt.de",
        live: "https://www.immowelt.de"
    },
    iwtat: {
        dev: "https://dev.immowelt.at",
        preview: "https://preview.immowelt.at",
        live: "https://www.immowelt.at"
    },
    li: {
        dev: "https://dev.logic-immo.com",
        preview: "https://preview.logic-immo.com",
        live: "https://www.logic-immo.com"
    },
    sl: {
        //`dev` and `preview` not available yet. See Unified Routing status: https://avivgroup.atlassian.net/browse/SEEKERWL-627
        dev: "https://dev.seloger.com",
        preview: "https://preview.seloger.com",
        live: "https://www.seloger.com"
    },
    sln: {
        dev: "https://dev.selogerneuf.com",
        preview: "https://preview.selogerneuf.com",
        live: "https://www.selogerneuf.com"
    },
    slp: {
        dev: "https://dev.myselogerpro.com",
        preview: "https://preview.myselogerpro.com",
        live: "https://www.myselogerpro.com"
    },
    iwb: {
        dev: "https://dev.immoweb.be",
        preview: "https://preview.immoweb.be",
        live: "https://www.immoweb.be"
    }
};

const isArrayValue = (value)=>{
    return Array.isArray(value);
};

const isLocalizedBrand = (brand)=>{
    return brand in LOCALIZED_BRANDS;
};

const createErrorMessage = ({ cause, solution }, options = {})=>{
    const finalPoint = options.insertFinalPoint ? "." : "";
    return `\n❌ ${cause}${finalPoint}\n✅ To solve the issue: ${solution}${finalPoint}`;
};

function assert(expectedCondition, errorMessage) {
    if (!expectedCondition) {
        throw new Error(errorMessage);
    }
}
function assertField(expectedCondition, errorInput) {
    assert(expectedCondition, createInvalidFieldMessage(errorInput));
}
const createInvalidFieldMessage = ({ id, solution })=>{
    return createErrorMessage({
        cause: `The \`${id}\` item contains invalid field`,
        solution
    });
};

/**
 * Extracts the remote type, capability code and component name from the given id.
 */ const getIdParts = ({ id })=>{
    /**
	 * Testing "app" | "mfe" here, so it's safe to use the `RemoteType` type.
	 *
	 * The simplified regex pattern is:
	 * `<remoteType>_<capabilityCode>_<componentName?>_<rest?.*>`
	 * The `<remoteId>` is everything after the first `_`.
	 */ const match = /^(?<remoteType>app|mfe)(?:_(?<capabilityCode>[^_]+))(?:_(?<componentName>.*))?$/.exec(id);
    assertField(match, {
        id,
        solution: "The `id` field must follow the two-segment or three-segment naming pattern (either `{app|mfe}_{capabilityCode}` or `{app|mfe}_{capabilityCode}_{componentName}` if a capability has multiple components)"
    });
    const { remoteType, capabilityCode, componentName } = match.groups;
    const remoteId = componentName ? `${capabilityCode}_${componentName}` : capabilityCode;
    return {
        remoteId,
        remoteType,
        capabilityCode,
        ...componentName && {
            componentName
        }
    };
};

const isRecordValue = (value)=>{
    return value !== null && typeof value === "object" && !Array.isArray(value);
};
const getRecordValue = (value, key)=>{
    return isRecordValue(value) ? value[key] : value;
};

const merge = (base, overload)=>{
    const overloadShallowClone = {
        ...overload
    };
    for(const key in overload){
        if (base && base[key] && isRecordValue(overloadShallowClone[key]) && isRecordValue(base[key])) {
            overloadShallowClone[key] = merge(base[key], overloadShallowClone[key]);
        }
    }
    return {
        ...base,
        ...overloadShallowClone
    };
};

const isBrandedPaths = (value)=>{
    return isRecordValue(value) && Object.keys(value).every((key)=>BRANDS.includes(key));
};
const isGenericPaths = (value)=>{
    return isArrayValue(value);
};
const isLocalizedPaths = (input)=>{
    return isRecordValue(input) && LOCALES.some((locale)=>locale in input);
};

/**
 * Sort component items by id.
 * @param input - Application or micro-frontend registry items.
 * @returns A `Map` instance containing an ordered pair of [id, item] value.
 */ const sortItemsById = (input)=>{
    const keys = Object.keys(input);
    return keys.sort((idA, idB)=>{
        return idA.localeCompare(idB, "en");
    }).reduce((output, id)=>{
        const application = input[id];
        if (application) {
            output.set(id, application);
        }
        return output;
    }, new Map());
};
/**
 * Sort application by path.
 * @param input - Application registry items.
 * @returns A `Map` instance containing an ordered pair of [path, applicationItem] value.
 *
 * TODO: Opportunity later to use it during the `transform` instead of `loading` phase to make the IaC output benefit from the same order than the JavaScript output.
 * To be confirmed with DevOps while starting the {@link https://github.com/axel-springer-kugawana/aviv_platform_unified_frontend_registry/issues/97 Unified Routing automation with the registry}.
 */ const sortApplicationsByPath = (input)=>{
    const applicationByPath = Object.values(input).reduce((output, application)=>{
        if (application) {
            application.routing.paths.forEach((path)=>{
                output[path] = application;
            });
        }
        return output;
    }, {});
    const paths = Object.keys(applicationByPath);
    return paths.sort((pathA, pathB)=>{
        return computePathScore(pathB) - computePathScore(pathA);
    }).reduce((output, path)=>{
        const application = applicationByPath[path];
        if (application) {
            output.set(path, application);
        }
        return output;
    }, new Map());
};
const staticSegmentConstruct = "[\\w-]+";
const extensionSegmentRegex = new RegExp(`\\.${staticSegmentConstruct}$`);
const dynamicSegmentScore = 1;
const extensionSegmentScore = 1;
const staticSegmentScore = 10;
/**
 * Sorting rank algorithm based on React Router to order a set of paths by specificity.
 * Algorithm has been modified to support partially dynamic paths
 *
 * @see {@link https://stackblitz.com/edit/vitejs-vite-2czqff2g?file=src%2Fsorting.ts,src%2FApp.tsx React Router Playground}
 * @see {@link https://github.com/remix-run/react-router/blob/abfa7867d7ec60af160c50178496adffd3b4558c/packages/router/utils.ts#L750 React Router v6.24.1}
 * @param path The path to compute sort ranking score from
 * @returns Ranking score (the higher is, the more the path is specific)
 */ const computePathScore = (path)=>{
    const segments = path.replace(/^\/+/, "").replace(/\/+$/, "").split("/");
    const penality = segments.some((segment)=>segment === "*") ? -dynamicSegmentScore - extensionSegmentScore - 1 : 0;
    const initialScore = segments.length + penality;
    return segments.reduce((score, segment)=>{
        if (segment === "*") return score;
        const isDynamicSegment = segment.startsWith(":");
        if (!isDynamicSegment || segment === "") return score + staticSegmentScore;
        // Cumulative scoring for partial constructs:
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (isDynamicSegment) score += dynamicSegmentScore;
        if (extensionSegmentRegex.test(segment)) score += extensionSegmentScore;
        return score;
    }, initialScore);
};

export { B2B_BRANDS, B2C_BRANDS, BRANDS, CAPABILITY_CODES, DOMAIN_BY_BRAND_STAGE, LOCALES, LOCALIZED_BRANDS, REMOTE_TYPES, STAGES, assert, assertField, createErrorMessage, getIdParts, getRecordValue, isArrayValue, isBrandedPaths, isGenericPaths, isLocalizedBrand, isLocalizedPaths, isRecordValue, merge, sortApplicationsByPath, sortItemsById };
